"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useBranches } from "@/hooks/api/useBranches";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type ShiftRow = {
  id: number;
  branch_id: number;
  user_id: number;
  opened_at: string;
  closed_at: string | null;
  opening_cash: number;
  closing_cash: number | null;
  cash_sales: number | null;
  card_sales: number | null;
  upi_sales: number | null;
};

export default function AdminShiftsPage() {
  const qc = useQueryClient();
  const { branchesEnabled } = useBranchesEnabled();
  const { data: branches } = useBranches();
  const [branchId, setBranchId] = useState("1");

  useEffect(() => {
    const first = branches?.[0]?.id;
    if (!branchesEnabled && first != null) {
      setBranchId(String(first));
    }
  }, [branches, branchesEnabled]);
  const [opening, setOpening] = useState("0");

  const { data, refetch } = useQuery({
    queryKey: ["admin", "shifts", branchId],
    queryFn: async () => {
      const { data: d } = await api.get<{ shifts: ShiftRow[] }>("/api/shifts", {
        params: { branch_id: branchId || undefined, open: "1" },
      });
      return d.shifts;
    },
  });

  const openShift = useMutation({
    mutationFn: async () => {
      await api.post("/api/shifts/open", {
        branch_id: Number(branchId),
        opening_cash: Number(opening),
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin", "shifts"] });
    },
  });

  const [closeId, setCloseId] = useState("");
  const [closingCash, setClosingCash] = useState("");
  const [cashSales, setCashSales] = useState("");
  const [cardSales, setCardSales] = useState("");
  const [upiSales, setUpiSales] = useState("");
  const [notes, setNotes] = useState("");

  const closeShift = useMutation({
    mutationFn: async () => {
      await api.post(`/api/shifts/${closeId}/close`, {
        closing_cash: Number(closingCash),
        cash_sales: cashSales === "" ? null : Number(cashSales),
        card_sales: cardSales === "" ? null : Number(cardSales),
        upi_sales: upiSales === "" ? null : Number(upiSales),
        notes: notes || null,
      });
    },
    onSuccess: async () => {
      setCloseId("");
      await qc.invalidateQueries({ queryKey: ["admin", "shifts"] });
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Shifts</h1>
        <p className="text-sm text-muted-foreground">Open shift, record closing totals.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open shift</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 max-w-sm">
          {branchesEnabled ? (
            <div className="grid gap-2">
              <Label>Branch id</Label>
              <Input value={branchId} onChange={(e) => setBranchId(e.target.value)} />
            </div>
          ) : null}
          <div className="grid gap-2">
            <Label>Opening cash</Label>
            <Input value={opening} onChange={(e) => setOpening(e.target.value)} />
          </div>
          <Button type="button" disabled={openShift.isPending} onClick={() => openShift.mutate()}>
            Open shift
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open shifts (this branch)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button type="button" variant="outline" size="sm" onClick={() => void refetch()}>
            Refresh
          </Button>
          <ul className="text-sm space-y-2">
            {(data ?? []).map((s) => (
              <li key={s.id} className="rounded border border-border/60 px-3 py-2">
                <span className="font-mono text-xs">#{s.id}</span> opened {s.opened_at} · float{" "}
                {s.opening_cash}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Close shift</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 max-w-md">
          <div className="grid gap-2">
            <Label>Shift id</Label>
            <Input value={closeId} onChange={(e) => setCloseId(e.target.value)} placeholder="from list above" />
          </div>
          <div className="grid gap-2">
            <Label>Closing cash</Label>
            <Input value={closingCash} onChange={(e) => setClosingCash(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Cash sales (optional)</Label>
            <Input value={cashSales} onChange={(e) => setCashSales(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Card sales (optional)</Label>
            <Input value={cardSales} onChange={(e) => setCardSales(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>UPI sales (optional)</Label>
            <Input value={upiSales} onChange={(e) => setUpiSales(e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Notes</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <Button
            type="button"
            variant="secondary"
            disabled={closeShift.isPending || !closeId}
            onClick={() => closeShift.mutate()}
          >
            Close shift
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
